import { inpOutlierNote } from './inpOutlierNote.js';
import { comparableLabSummary, inpRepeatedObservation } from './comparableLabSummary.js';

// =========================================================================
// CANONICAL FINDING for the AI narrative guardrail.
//
// Derives ONE structured deterministic conclusion (cause + evidence level)
// from the export payload's deep results. The AI narrative receives this as
// its ONLY permitted causal conclusion — it may interpret and explain it,
// but must never introduce another root cause or strengthen its certainty.
//
// Evidence levels:
//   observed  — measured directly and consistently (may be stated as fact)
//   supported — one valid lab measurement points to it (strong indication)
//   variable  — the dominant cause flips between stored runs (no single cause)
//   candidate — indirect signal only (must be phrased as "to verify")
//   unknown   — no valid deep measurement for the primary metric
//
// Used by: DiagnosticsPanel export, quickAudit in-tab audit, and the
// server-side complex-audit orchestrator (loaded like the other client
// modules). Pure function, no side effects, defensive against old payloads.
// =========================================================================

import { hasValidInpMeasurement } from './inpMeasurementValidity.js';

export const EVALUATION_METHODOLOGY_VERSION = 'evidence-consistency-v1';

const COMP_LABELS = {
  inputDelay: { cs: 'čekání na volné hlavní vlákno (input delay)', en: 'waiting for a free main thread (input delay)' },
  processingTime: { cs: 'zpracování event handlerů (processing time)', en: 'event handler processing (processing time)' },
  presentationDelay: { cs: 'vykreslení odezvy (presentation delay)', en: 'rendering the response (presentation delay)' }
};

const PHASE_LABELS = {
  ttfb: { cs: 'odpověď serveru (TTFB)', en: 'server response (TTFB)' },
  resourceLoad: { cs: 'stažení LCP zdroje', en: 'downloading the LCP resource' },
  renderDelay: { cs: 'prodleva vykreslení LCP', en: 'LCP render delay' }
};

// v != null guard first — Number(null) === 0 would turn an undetermined
// value (e.g. an unknown LCP phase) into a false-known zero.
const num = (v) => (v != null && Number.isFinite(Number(v)) ? Number(v) : null);
const fmtMsNumber = (v) => {
  const n = num(v);
  if (n == null) return null;
  return String(Math.round(n * 10) / 10);
};

const asSentence = (value) => {
  const text = String(value || '').trim();
  if (!text) return null;
  const capitalized = text.charAt(0).toLocaleUpperCase() + text.slice(1);
  return /[.!?]$/.test(capitalized) ? capitalized : `${capitalized}.`;
};

// Canonical fields are also rendered as separate paragraphs, but some report
// surfaces compose them inline. Normalize that boundary once so missing and
// variable states cannot turn into run-on fragments.
export const canonicalFindingSentences = (finding) => [
  asSentence(finding?.shows),
  asSentence(finding?.cannotDetermine),
  asSentence(finding?.verifyNext)
].filter(Boolean);

// Interpretation only: never change the persisted measurements or infer
// missing conditions. A phase and an interaction target are not a code cause.
export function evaluateInpEvidence(payload = {}) {
  const deep = payload.deepINPResults;
  const spread = deep?.labRunSpread;
  const valid = hasValidInpMeasurement(deep);
  const summary = comparableLabSummary(spread);
  const values = summary?.values || [];
  const median = summary?.median ?? null;
  const labValue = valid ? (median != null && median > 0 ? median : num(deep.inp)) : null;
  const profiles = new Set();
  for (const attempt of spread?.attempts || []) {
    if (attempt.valid === false || ['failed', 'partial'].includes(attempt.status)) continue;
    const b = attempt.breakdown;
    if (!b) continue;
    const rows = Object.keys(COMP_LABELS).map(k => [k, num(b[k])]);
    if (rows.some(([, v]) => v == null)) continue;
    rows.sort((a, b) => b[1] - a[1]);
    if (rows[0][1] - rows[1][1] >= 5) profiles.add(rows[0][0]);
  }
  const variable = spread?.components?.dominance?.variable === true || profiles.size > 1;
  const normalizeDevice = d => {
    const value = typeof d === 'string' ? d.toLowerCase() : null;
    return value === 'phone' ? 'mobile' : value;
  };
  const fieldDevice = normalizeDevice(payload.fieldDevice || payload.devicePriority?.primaryDevice || payload.device);
  const labDevice = normalizeDevice(spread?.conditions?.device || spread?.device || deep?.device);
  const comparableDevice = fieldDevice && labDevice ? fieldDevice === labDevice : null;
  return {
    methodologyVersion: EVALUATION_METHODOLOGY_VERSION,
    labValue,
    variable,
    profiles: [...profiles],
    comparableDevice,
    reproduction: labValue == null ? 'unavailable' : labValue > 200 ? 'reproduced' : values.some(v => v > 200) ? 'intermittent' : 'not-reproduced',
    // Event Timing identifies latency phases, not the responsible DOM subtree,
    // script/vendor or a shared template implementation.
    confirmedCause: false,
    taskKind: 'diagnostic'
  };
}

export function deriveCanonicalFinding(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const lang = payload.language === 'en' ? 'en' : 'cs';
  const metric = String(payload.metric || '').toUpperCase();
  if (!metric) return null;

  const unknown = (showsCs, showsEn) => ({
    metric,
    cause: null,
    evidenceLevel: 'unknown',
    shows: asSentence(lang === 'cs' ? showsCs : showsEn),
    cannotDetermine: asSentence(lang === 'cs'
      ? 'příčinu nelze z tohoto měření určit'
      : 'the cause cannot be determined from this measurement'),
    verifyNext: asSentence(lang === 'cs' ? 'zopakovat hloubkový test' : 're-run the deep test')
  });

  // Deep test for the primary metric failed → no causal claim allowed.
  if (Array.isArray(payload.failedDeepMetrics) && payload.failedDeepMetrics.includes(metric)) {
    return unknown(
      'hloubkový test primární metriky selhal (timeout/chyba)',
      'the deep test of the primary metric failed (timeout/error)'
    );
  }

  if (metric === 'INP') {
    const deep = payload.deepINPResults || null;
    const evaluation = evaluateInpEvidence(payload);
    if (evaluation.labValue == null) {
      return unknown('platná interakce nebyla změřena', 'no valid interaction was measured');
    }
    const sp = deep?.labRunSpread;
    const comps = sp?.components;
    const dom = comps?.dominance;

    // Cross-run dominance flip → variable profile, no single cause.
    // dominance.primary/secondary use the diagnostics route's short enums
    // ('input' | 'processing' | 'presentation') — map them to component keys.
    const DOM_KEY = { input: 'inputDelay', processing: 'processingTime', presentation: 'presentationDelay' };
    if (evaluation.variable) {
      const p = COMP_LABELS[evaluation.profiles[0] || DOM_KEY[dom?.primary]]?.[lang] || 'input / processing';
      const s = COMP_LABELS[evaluation.profiles[1] || DOM_KEY[dom?.secondary]]?.[lang] || 'presentation';
      return {
        metric,
        cause: null,
        evidenceLevel: 'variable',
        shows: [inpRepeatedObservation(sp, lang), asSentence(lang === 'cs'
          ? `dominantní složka INP se mezi ${sp.runs} uloženými běhy střídá: v části běhů ${p}, v jiné části ${s}`
          : `the dominant INP component flips across ${sp.runs} stored runs: ${p} in some runs, ${s} in others`)].filter(Boolean).join(' '),
        cannotDetermine: asSentence(lang === 'cs'
          ? 'Jednu hlavní příčinu pomalé odezvy z těchto běhů nelze určit.'
          : 'A single main cause of the slow responsiveness cannot be determined from these runs.'),
        verifyNext: asSentence(lang === 'cs'
          ? 'profilovat konkrétní pomalé interakce v Chrome DevTools (Performance)'
          : 'profile the specific slow interactions in Chrome DevTools (Performance)')
      };
    }

    // Median components across ≥2 runs, or a single valid worst interaction.
    const src = (comps && sp.runs >= 2) ? comps : (deep?.worstInteraction || null);
    const parts = src ? {
      inputDelay: num(src.inputDelay),
      processingTime: num(src.processingTime),
      presentationDelay: num(src.presentationDelay)
    } : null;
    const valid = parts && Object.values(parts).every(v => v != null) &&
      Object.values(parts).some(v => v > 0);
    if (!valid) {
      return unknown(
        `lab INP ${evaluation.labValue} ms; použitelný rozpad input/processing/presentation chybí`,
        `lab INP ${evaluation.labValue} ms; a usable input/processing/presentation breakdown is missing`
      );
    }
    const dominant = Object.entries(parts).sort((a, b) => b[1] - a[1])[0][0];
    const multiRun = !!(comps && sp.runs >= 2);
    const seriesValues = (sp?.values || []).map(num).filter(v => v != null && v > 0);
    const overThresholdRuns = seriesValues.filter(v => v > 200).length;
    return {
      metric,
      cause: null,
      evidenceLevel: 'candidate',
      shows: inpRepeatedObservation(sp, lang) + ' ' + (lang === 'cs'
        ? `${evaluation.reproduction === 'intermittent' ? `Problém se v sérii projevil přerušovaně: ${overThresholdRuns} z ${seriesValues.length} validních běhů překročil${overThresholdRuns === 1 ? '' : 'y'} 200 ms, i když medián zůstal v dobrém pásmu.${inpOutlierNote(sp, lang)} ` : ''}Rozpad ${multiRun ? `mediánu z ${sp.runs} běhů` : 'jedné změřené interakce'}: input delay ${fmtMsNumber(parts.inputDelay)} ms, processing ${fmtMsNumber(parts.processingTime)} ms, presentation ${fmtMsNumber(parts.presentationDelay)} ms.`
        : `${evaluation.reproduction === 'intermittent' ? `The problem reproduced intermittently in the series: ${overThresholdRuns} of ${seriesValues.length} valid runs exceeded 200 ms even though the median remained in the good band.${inpOutlierNote(sp, lang)} ` : ''}Breakdown of ${multiRun ? `the median across ${sp.runs} runs` : 'one measured interaction'}: input delay ${fmtMsNumber(parts.inputDelay)} ms, processing ${fmtMsNumber(parts.processingTime)} ms, presentation ${fmtMsNumber(parts.presentationDelay)} ms.`),
      cannotDetermine: lang === 'cs'
        ? `Konkrétní příčina není potvrzena; dominantní fáze ${COMP_LABELS[dominant][lang]} není atribuce ke kódu ani šabloně.`
        : `The specific cause is not confirmed; the dominant phase ${COMP_LABELS[dominant][lang]} is not code or template attribution.`,
      verifyNext: lang === 'cs'
        ? 'Další krok: profilovat pomalou interakci a porovnat opakované běhy ve stejných podmínkách.'
        : 'Next step: profile a slow interaction and compare repeated runs under matching conditions.'
    };
  }

  if (metric === 'LCP') {
    const ph = payload.deepLCPResults?.phases;
    const parts = ph ? {
      ttfb: num(ph.ttfb),
      resourceLoad: num(ph.resourceLoad),
      renderDelay: num(ph.renderDelay)
    } : null;
    const valid = parts && Object.values(parts).every(v => v != null) &&
      Object.values(parts).some(v => v > 0);
    if (!valid) {
      return unknown(
        'hloubkový test nezachytil rozpad LCP fází',
        'the deep test captured no LCP phase breakdown'
      );
    }
    const dominant = Object.entries(parts).sort((a, b) => b[1] - a[1])[0][0];
    return {
      metric,
      cause: PHASE_LABELS[dominant][lang],
      evidenceLevel: 'supported',
      shows: lang === 'cs'
        ? `fáze LCP jednoho hloubkového běhu: TTFB ${parts.ttfb} ms, stažení zdroje ${parts.resourceLoad} ms, prodleva vykreslení ${parts.renderDelay} ms`
        : `LCP phases of one deep run: TTFB ${parts.ttfb} ms, resource load ${parts.resourceLoad} ms, render delay ${parts.renderDelay} ms`,
      cannotDetermine: lang === 'cs'
        ? 'zda je rozložení fází stabilní napříč návštěvami (jeden lab běh)'
        : 'whether the phase split is stable across visits (single lab run)',
      verifyNext: lang === 'cs'
        ? 'ověřit dominantní fázi opakovaným během'
        : 'verify the dominant phase with a repeated run'
    };
  }

  if (metric === 'CLS') {
    // Culprit consensus across stored runs beats a single-run top culprit.
    const freq = payload.deepCLSResults?.labRunSpread?.culpritFrequency;
    const totalRuns = num(payload.deepCLSResults?.labRunSpread?.runs);
    const seenIn = num(Array.isArray(freq) ? freq[0]?.seenIn : null);
    // Counts must describe a real, bounded run population. Missing, negative,
    // non-finite, fractional or impossible counts cannot support attribution.
    const credibleCounts = Number.isSafeInteger(totalRuns) && totalRuns > 0
      && Number.isSafeInteger(seenIn) && seenIn > 0 && seenIn <= totalRuns;
    if (Array.isArray(freq) && freq.length > 0 && freq[0]?.selector && credibleCounts) {
      const top = freq[0];
      // culpritFrequency entries carry `seenIn` (runs the culprit appeared in).
      const consensus = totalRuns >= 2 && seenIn >= Math.ceil(totalRuns / 2);
      return {
        metric,
        cause: lang === 'cs'
          ? `posun layoutu způsobený prvkem ${top.selector}`
          : `layout shift caused by element ${top.selector}`,
        evidenceLevel: consensus ? 'observed' : 'supported',
        shows: lang === 'cs'
          ? `prvek ${top.selector} byl hlavním zdrojem posunu v ${seenIn} z ${totalRuns} uložených běhů`
          : `element ${top.selector} was the main shift source in ${seenIn} of ${totalRuns} stored runs`,
        cannotDetermine: null,
        verifyNext: null
      };
    }
    return unknown(
      'hloubkový test neurčil konkrétní zdroj posunu layoutu',
      'the deep test identified no specific layout-shift source'
    );
  }

  if (metric === 'TTFB') {
    const v = num(payload.value);
    if (v == null) {
      return unknown('hodnota TTFB není k dispozici', 'the TTFB value is unavailable');
    }
    return {
      metric,
      cause: lang === 'cs' ? 'pomalá odpověď serveru' : 'slow server response',
      evidenceLevel: 'observed',
      shows: lang === 'cs'
        ? `server odpovídá za ${Math.round(v)} ms (p75)`
        : `the server responds in ${Math.round(v)} ms (p75)`,
      cannotDetermine: lang === 'cs'
        ? 'zda zdržuje backend, cache nebo přesměrování (vyžaduje serverovou diagnostiku)'
        : 'whether the backend, caching or redirects are at fault (requires server-side diagnostics)',
      verifyNext: lang === 'cs'
        ? 'změřit čas generování stránky na backendu a zkontrolovat cache/CDN'
        : 'measure backend page-generation time and check caching/CDN'
    };
  }

  return null;
}
